import limitValue from "../../../utils/limit-value";
import getStepStartTime from "./get-step-start-time";
import getOffsetStartTime from "./get-offset-start-time";
import getProgress from "./get-progress";
import loopProperties from "./loop-properties";
import onStepStatus from "./on-step-status";
import onTimelineStatus from "./on-timeline-status";
import getPercents from "./percents/get-percents";
import getPercentsCoordinates from "./percents/get-percents-coordinates";
import setActiveProperties from "./set-active-properties";

/**
 * Compute interval timeline
 * @param {TemporaryDataProps} data
 * @param {Number} now
 */
const computeTimeline = (data, now) => {
  const main = /** @type {MainClassProps} */ (data.get("main"));
  const trigger = /** @type {TriggerProps} */ (data.get("trigger"));
  const timeline = /** @type {TimelineMapProps} */ (data.get("timeline"));

  /** Trigger is coordinate-based */
  const coords = /** @type {Boolean} */ (trigger.get("is-coordinates-based"));

  /** Current enter or leave mode */
  const enter = /** @type {Boolean} */ (trigger.get("enter"));

  /** Trigger mode switched */
  const switched = /** @type {Boolean} */ (trigger.get("mode-switch"));

  /** All steps */
  const steps = /** @type {Map<Number, StepProps>} */ (trigger.get("t_steps"));

  /** Non limited percents (parallax) */
  const nonLimited = trigger.get("is-non-limited");

  /** Global original timeline startTime from trigger */
  const TIME = getOffsetStartTime(trigger, timeline, enter, now);

  /** Global timeline duration */
  const DURATION = coords
    ? -1
    : /** @type {Number} */ (timeline.get("o_duration"));

  /** Global timeline offset duration */
  const OFFSET = /** @type {Number} */ (timeline.get("offsets-duration"));

  /** Timeline has spring easing */
  const spring = /** @type {Boolean} */ (timeline.get("has-spring"));

  /** Timeline is alternating */
  const isAlternating = timeline.get("is-alternating");

  /** Time ratio - in the case when time offsets and duration are defined together  */
  const ratio = DURATION / OFFSET;

  /** Global timeline percents (to get an overall percents) */
  const PERCENTS = coords
    ? getPercentsCoordinates(main, trigger, timeline)
    : getPercents(timeline, TIME, now, enter, spring, DURATION);

  /** Get the overall percents */
  const OVERALL = /** @type {Number} */ (timeline.get("percents-overall"));

  /** Compute a tick */
  now = coords ? OVERALL * 100 : now;

  /** Loop over all steps */
  for (const [number, step] of steps) {
    /** Set current timeline percents state on mode switch */
    if (switched && !isAlternating) {
      step.set("switch-percents", OVERALL);

      if (coords) {
        setActiveProperties(trigger, main, step);
      }
    }

    /** Duration of each step */
    const duration = /** @type {Number} */ (step.get("step-duration")) * ratio;

    /** Start time of each step */
    let startTime = getStepStartTime(
      TIME,
      DURATION,
      step,
      enter,
      coords,
      ratio
    );

    /** Percentage of each step */
    const percents = getPercents(step, startTime, now, enter, spring, duration);

    /** Unlimited percents */
    const unlimited = /** @type {Number} */ (step.get("percents-unlimited"));

    /** Skip the step if not active */
    if (!coords && enter && unlimited < 0) {
      continue;
    }

    /** If the step starts in enter (percents = 0) or leave (percents = 1), activate all properties in the step */
    if ((enter && unlimited === 0) || (!enter && unlimited === 1)) {
      setActiveProperties(trigger, main, step);
    }

    /** Limit (clamp) percents to 0-1 */
    const limited = nonLimited ? unlimited : limitValue(percents);

    /** Step progress based on percents */
    const progress = getProgress(step, limited, enter, coords, spring);

    /** Properties of the current step */
    const properties = /** @type {Map<String, any>} */ (step.get("properties"));

    /** Loop all properties of the current step */
    loopProperties(
      data,
      properties,
      switched,
      enter,
      unlimited,
      progress,
      step
    );

    /** Do stuff on step start/finish */
    onStepStatus(data, step, unlimited, enter, coords, number);
  }

  /** Do stuff on timeline start/finish */
  onTimelineStatus(data, enter, PERCENTS, coords);

  trigger.set("mode-switch", false);
};

export default computeTimeline;
